import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";

import { ButtonComponent } from "@vg-constellation/angular-17/button";
import { IconComponent, IconModule } from "@vg-constellation/angular-17/icon";
import { LinkComponent } from "@vg-constellation/angular-17/link";
import { ModalDialogComponent } from "@vg-constellation/angular-17/modal-dialog";
import { HintErrorComponent } from "@vg-constellation/angular-17/hint-error";
import { DropdownMenuItem } from "@vg-constellation/angular-17/dropdown";

import {
  Beneficiary,
  ExistingBeneficiary,
  NewBeneficiary,
  BeneficiarySectionType,
  BeneficiaryState,
  calculateDistributionTotal,
  createEmptyBeneficiaryState,
  getBeneficiaryDisplayName,
  EditBeneficiaryPayload,
  EditTrustBeneficiaryPayload,
} from "@models/beneficiary.model";
import { BeneficiaryCardComponent } from "../beneficiary-card/beneficiary-card.component";
import { InputBeneficiaryCardComponent } from "../input-beneficiary-card/input-beneficiary-card.component";
import { Trust } from "@models/estateProfile.model";
import { AccountType } from "../../types/AccountType";
import { Person, getPersonDisplayName } from "@services/zok";
import { BeneficiaryZokService } from "@services/beneficiary-zok";
import { BeneficiaryDuplicateDetectionService } from "@services/beneficiary-duplicate-detection/beneficiary-duplicate-detection.service";
import { WebLogger } from "@weps/shared-http";
import * as content from "@services/content/guided-flow/account-details-content";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import { buildSettlorItems } from "@services/utils/trust-settlor.utils";

@Component({
  selector: "app-beneficiary-section",
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    IconModule,
    LinkComponent,
    ModalDialogComponent,
    HintErrorComponent,
    BeneficiaryCardComponent,
    InputBeneficiaryCardComponent,
  ],
  templateUrl: "./beneficiary-section.component.html",
  styleUrls: ["./beneficiary-section.component.scss"],
})
export class BeneficiarySectionComponent implements OnInit {
  private readonly beneficiaryZokService = inject(BeneficiaryZokService);
  private readonly dupDetection = inject(BeneficiaryDuplicateDetectionService);
  private readonly globalStore = inject(GlobalStateStore);

  private logEvent(
    eventType: string,
    payload: Record<string, unknown> = {},
    level: "info" | "warn" | "error" = "info",
  ): void {
    WebLogger.log({
      message: `Beneficiary section: ${eventType}`,
      level,
      payload: { event_type: eventType, ...payload },
    });
  }

  @Input() accountType: AccountType;
  @Input() initialBeneficiaryState: BeneficiaryState | null = null;
  @Input() payableOnDeathEnabled = false;

  @Output() beneficiaryStateChange = new EventEmitter<BeneficiaryState>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() helpClick = new EventEmitter<void>();
  @Output() savingChange = new EventEmitter<boolean>();

  @ViewChild("removeModal") removeModal: ModalDialogComponent;
  @ViewChild("duplicateModal") duplicateModal: ModalDialogComponent;
  @ViewChild(InputBeneficiaryCardComponent) activeInputCard: InputBeneficiaryCardComponent;

  protected readonly content = content;

  // Beneficiary data
  beneficiaryState: BeneficiaryState = createEmptyBeneficiaryState();

  // UI state — only one section can be adding at a time
  addingSection: BeneficiarySectionType | null = null;
  isSaving = false;
  editingBeneficiary: Beneficiary | null = null;
  editingSection: BeneficiarySectionType | null = null;
  beneficiaryToRemove: Beneficiary | null = null;
  removeSection: BeneficiarySectionType | null = null;
  protected inputCardResetKey = 0; // Used to force component recreation

  // Duplicate detection state
  pendingBeneficiary: Beneficiary | null = null;
  pendingSaveSection: BeneficiarySectionType | null = null;
  duplicateMatchName = "";

  get pendingBeneficiaryDisplayName(): string {
    return this.getBeneficiaryDisplayName(this.pendingBeneficiary);
  }

  get duplicateDetectionMessage(): string {
    return `The person you tried to add, ${this.pendingBeneficiaryDisplayName}, has the same date of birth as someone associated with your accounts, ${this.duplicateMatchName}.`;
  }

  // Validation state
  showRequiredError = false;
  showIncompleteError = false;
  showPrimaryDistributionError = false;
  showBackupDistributionError = false;
  showDuplicatePersonError = false;
  showCreatePersonError = false;
  showCreateTrustError = false;
  showHouseholdLoadError = false;
  showZeroDistributionError = false;

  // Household members loaded from ZOK
  householdMembers: DropdownMenuItem[] = [];
  householdPeople: Person[] = [];
  householdLoaded = false;

  trusts: Trust[] = [];
  settlors: DropdownMenuItem[] = [];

  private buildBeneSettlorsList(): DropdownMenuItem[] {
    const estateProfile = this.globalStore.estateProfile?.();
    if (!estateProfile) return [];
    const primaryClient = estateProfile.primaryClient;
    const spouses = this.globalStore.findAllSpouseData();
    return buildSettlorItems(primaryClient, spouses, "");
  }

  private refreshBeneTrusts(): void {
    this.trusts = this.globalStore.getTrusts();
  }

  private refreshBeneSettlors(): void {
    this.settlors = this.buildBeneSettlorsList();
  }

  ngOnInit(): void {
    // Restore persisted beneficiary state from guided flow
    // Person data (name, DOB, state) is looked up from householdPeople by householdMemberId
    if (
      this.initialBeneficiaryState &&
      this.hasPersistedBeneficiaries(this.initialBeneficiaryState)
    ) {
      this.beneficiaryState = { ...this.initialBeneficiaryState };
      this.dupDetection.rebuildFromState(this.beneficiaryState);
    }

    this.loadHouseholdMembers();
    this.refreshBeneTrusts();
    this.refreshBeneSettlors();

    // Check validation on init to show warnings for orphaned beneficiaries
    this.updateDistributionErrors();

    // Only auto-open the input card when there are no beneficiaries to display
    if (!this.hasBeneficiaries) {
      this.addingSection = "PRIMARY";
    }
  }

  private hasPersistedBeneficiaries(state: BeneficiaryState): boolean {
    return state.primaryBeneficiaries?.length > 0 || state.backupBeneficiaries?.length > 0;
  }

  private loadHouseholdMembers(): void {
    this.beneficiaryZokService.loadHouseholdMembers(
      (people) => {
        this.applyHouseholdPeople(people);
      },
      () => {
        this.householdMembers = [];
        this.showHouseholdLoadError = true;
      },
    );
  }

  /**
   * Reads the latest household people from the store and updates local state.
   * Use after the service has already mutated the store (create/update person).
   */
  private syncHouseholdFromStore(): void {
    const people = this.globalStore.householdPeople?.() || [];
    this.applyHouseholdPeople(people);
  }

  private applyHouseholdPeople(people: Person[]): void {
    this.householdPeople = people;
    this.householdMembers = people.map((person) => ({
      labelText: getPersonDisplayName(person),
      id: person.householdMemberId,
    }));
    this.showHouseholdLoadError = false;
    this.householdLoaded = true;
  }

  get primaryTotal(): number {
    return calculateDistributionTotal(this.beneficiaryState.primaryBeneficiaries);
  }

  get backupTotal(): number {
    return calculateDistributionTotal(this.beneficiaryState.backupBeneficiaries);
  }

  get hasBeneficiaries(): boolean {
    return (
      this.beneficiaryState.primaryBeneficiaries.length > 0 ||
      this.beneficiaryState.backupBeneficiaries.length > 0
    );
  }

  /**
   * Looks up Person data from householdPeople by householdMemberId.
   * Returns null if not found.
   */
  getPersonByMemberId(memberId: string | null): Person | null {
    if (!memberId) return null;
    return this.householdPeople.find((p) => p.householdMemberId === memberId) || null;
  }

  getBeneficiaryDisplayName(beneficiary: Beneficiary | null): string {
    return getBeneficiaryDisplayName(beneficiary, this.householdPeople, this.trusts);
  }

  get isPrimaryValid(): boolean {
    // If POD is not enabled, beneficiaries are not required - always valid
    if (!this.payableOnDeathEnabled) {
      return true;
    }
    // If POD is enabled, primary beneficiaries must total exactly 100%
    // Empty list is invalid (0% ≠ 100%), which catches orphaned beneficiary cases
    return this.primaryTotal === 100;
  }

  get isBackupValid(): boolean {
    return this.beneficiaryState.backupBeneficiaries.length === 0 || this.backupTotal === 100;
  }

  get hasZeroDistribution(): boolean {
    const allBenes = [
      ...this.beneficiaryState.primaryBeneficiaries,
      ...this.beneficiaryState.backupBeneficiaries,
    ];
    return allBenes.some((b) => b.distributionPercentage < 1);
  }

  get isInputCardOpen(): boolean {
    return this.addingSection !== null || this.editingBeneficiary !== null;
  }

  trackByBeneficiary(index: number, bene: Beneficiary): string {
    return bene.householdMemberId || `bene-${index}`;
  }

  handleHelpClick(): void {
    this.helpClick.emit();
  }

  // --- Add beneficiary ---

  handleAdd(section: BeneficiarySectionType): void {
    if (this.isInputCardOpen) {
      this.showIncompleteError = true;
      return;
    }
    if (section === "CONTINGENT" && this.beneficiaryState.primaryBeneficiaries.length === 0) {
      this.logEvent("backup_blocked_no_primary", {}, "warn");
      return;
    }
    this.showIncompleteError = false;
    this.addingSection = section;
  }

  // --- Save beneficiary (from InputBeneficiaryCard) ---

  handleSave(bene: Beneficiary, section: BeneficiarySectionType): void {
    bene.type = section;

    // DOB duplicate check: only for genuinely new persons (not existing household members)
    if (!this.editingBeneficiary && bene.kind === "new") {
      const matchedPerson = this.dupDetection.findDuplicateInHousehold(bene, this.householdPeople);
      if (matchedPerson) {
        this.pendingBeneficiary = bene;
        this.pendingSaveSection = section;
        this.duplicateMatchName = getPersonDisplayName(matchedPerson);
        this.duplicateModal?.openModalDialog();
        return;
      }
    }

    // If new trust, create in global store and commit directly
    if (bene.kind === "new" && bene.trustData) {
      this.createBeneTrustAndSave(bene, section);
    } else if (bene.kind === "new") {
      // New person — create in ZOK first
      this.createPersonInZok(bene, (savedBene) => this.commitSave(savedBene, section));
    } else {
      this.commitSave(bene, section);
    }
  }

  /**
   * New handler for edit payloads with separate person data
   */
  handleSaveEdit(payload: EditBeneficiaryPayload, section: BeneficiarySectionType): void {
    this.setSaving(true);

    this.beneficiaryZokService.updatePersonInZok(
      payload.beneficiary.householdMemberId,
      payload.personData,
      this.householdPeople,
      () => {
        // Update beneficiary in state
        const list = this.getBeneficiariesForSection(section);
        const editIndex = list.indexOf(this.editingBeneficiary!);
        if (editIndex >= 0) {
          list[editIndex] = payload.beneficiary;
        }
        // Service already updated the store — sync local state from it
        this.syncHouseholdFromStore();
        this.setSaving(false);
        this.closeInputCard();
        this.emitState();
      },
      () => {
        this.setSaving(false);
        this.showCreatePersonError = true;
      },
    );
  }

  /**
   * Handle trust beneficiary edit — persists to ROB eagerly (matching person edit flow),
   * then updates the local store and UI state on success.
   */
  handleSaveEditTrust(payload: EditTrustBeneficiaryPayload, section: BeneficiarySectionType): void {
    const { beneficiary, trustData } = payload;
    const settlorEnum = this.mapSettlorIdToEnum(trustData.settlor);
    const settlors = this.mapSettlorIdToStructure(trustData.settlor);

    this.setSaving(true);

    this.beneficiaryZokService.updateTrustInRob(
      beneficiary.householdMemberId,
      trustData,
      settlorEnum,
      () => {
        // Update the trust in the local global store on ROB success
        this.globalStore.updateTrust(beneficiary.householdMemberId, {
          legalName: trustData.legalName,
          displayName: trustData.displayName,
          trustType: trustData.trustType,
          settlors,
        });

        // Update beneficiary in state
        const list = this.getBeneficiariesForSection(section);
        const editIndex = list.indexOf(this.editingBeneficiary!);
        if (editIndex >= 0) {
          list[editIndex] = beneficiary;
        }

        this.refreshBeneTrusts();
        this.setSaving(false);
        this.closeInputCard();
        this.emitState();

        this.logEvent("beneficiary_trust_edited", {
          trust_id: beneficiary.householdMemberId,
          legal_name: trustData.legalName,
          section,
        });
      },
      (err) => {
        this.logEvent(
          "beneficiary_trust_edit_failed",
          { error: err instanceof Error ? err.message : String(err) },
          "error",
        );
        this.setSaving(false);
        this.showCreateTrustError = true;
      },
    );
  }

  private commitSave(bene: Beneficiary, section: BeneficiarySectionType): void {
    const list = this.getBeneficiariesForSection(section);

    if (this.editingBeneficiary && this.editingSection === section) {
      const editIndex = list.indexOf(this.editingBeneficiary);
      if (editIndex >= 0) {
        this.dupDetection.remove(list[editIndex]);
        list[editIndex] = bene;
        this.dupDetection.add(bene);
      }
    } else {
      // Business rule: Same trust can be both account owner AND beneficiary on the same account.
      // This is a valid estate planning scenario (confirmed 2026-07-13).
      if (this.dupDetection.isDuplicateInSection(bene, list)) {
        this.showDuplicatePersonError = true;
        return;
      }
      list.push(bene);
      this.dupDetection.add(bene);
    }

    this.closeInputCard();
    this.emitState();
  }

  // --- Trust Creation ---

  private createBeneTrustAndSave(bene: NewBeneficiary, section: BeneficiarySectionType): void {
    if (!bene.trustData) return;

    const settlorId = bene.trustData.settlor;
    const settlorEnum = this.mapSettlorIdToEnum(settlorId);
    const settlors = this.mapSettlorIdToStructure(settlorId);

    this.setSaving(true);

    // Create trust via ROB to get a real householdMemberId for MEZ persistence
    this.beneficiaryZokService.createTrustInRob(
      bene.trustData,
      settlorEnum,
      section,
      (savedBene) => {
        // Also add to local state store for immediate UI display
        this.globalStore.createTrust({
          householdMemberId: savedBene.householdMemberId,
          legalName: bene.trustData!.legalName,
          displayName: bene.trustData!.displayName,
          trustType: bene.trustData!.trustType,
          settlors,
        });

        this.logEvent("beneficiary_trust_created", {
          trust_id: savedBene.householdMemberId,
          legal_name: bene.trustData!.legalName,
          section,
        });

        this.refreshBeneTrusts();
        this.setSaving(false);
        this.commitSave(savedBene, section);
      },
      (err) => {
        this.logEvent(
          "beneficiary_trust_create_failed",
          { error: err instanceof Error ? err.message : String(err) },
          "error",
        );
        this.setSaving(false);
        this.showCreateTrustError = true;
      },
    );
  }

  private mapSettlorIdToEnum(settlorId: string): "PRIMARY" | "SPOUSE" | "JOINT" {
    if (settlorId === "JOINT") return "JOINT";
    if (settlorId === "PRIMARY") return "PRIMARY";
    // Any other value is a spouse UUID — ROB uses "SPOUSE" enum (not "SECONDARY")
    return "SPOUSE";
  }

  private mapSettlorIdToStructure(
    settlorId: string,
  ): { memberType: string; householdMemberId: string }[] {
    if (settlorId === "JOINT") return this.buildJointSettlors();
    return [
      {
        memberType: "PERSON",
        householdMemberId: settlorId === "PRIMARY" ? this.getPrimaryClientId() : settlorId,
      },
    ];
  }

  private getPrimaryClientId(): string {
    const estateProfile = this.globalStore.estateProfile?.();
    return (
      estateProfile?.primaryClient?.uuid || estateProfile?.primaryClient?.piClientId || "PRIMARY"
    );
  }

  private buildJointSettlors(): { memberType: string; householdMemberId: string }[] {
    const primaryId = this.getPrimaryClientId();
    const spouses = this.globalStore.findAllSpouseData();
    const spouseId = spouses[0]?.uuid || "SECONDARY";
    return [
      { memberType: "PERSON", householdMemberId: primaryId },
      { memberType: "PERSON", householdMemberId: spouseId },
    ];
  }

  // --- ZOK Integration ---

  private createPersonInZok(
    bene: NewBeneficiary,
    onSuccess: (bene: ExistingBeneficiary) => void,
  ): void {
    this.setSaving(true);

    this.beneficiaryZokService.createPersonInZok(
      bene,
      (savedBene) => {
        this.syncHouseholdFromStore();
        this.setSaving(false);
        onSuccess(savedBene);
      },
      () => {
        this.setSaving(false);
        this.showCreatePersonError = true;
      },
    );
  }

  private setSaving(value: boolean): void {
    this.isSaving = value;
    this.savingChange.emit(value);
  }

  // --- Cancel ---

  handleCancelInput(): void {
    this.closeInputCard();

    // Reset to initial state if no beneficiaries exist (same as delete logic)
    if (!this.hasBeneficiaries) {
      this.inputCardResetKey++; // Force complete component reset
      this.addingSection = "PRIMARY";
    }
  }

  private closeInputCard(): void {
    this.addingSection = null;
    this.editingBeneficiary = null;
    this.editingSection = null;
    this.showIncompleteError = false;
    this.showDuplicatePersonError = false;
    this.showCreatePersonError = false;
    this.showCreateTrustError = false;
  }

  // --- Edit ---

  handleEdit(bene: Beneficiary, section: BeneficiarySectionType): void {
    if (this.isInputCardOpen) {
      this.showIncompleteError = true;
      return;
    }
    this.logEvent("beneficiary_edit_start", {
      household_member_id: bene.householdMemberId,
      section,
    });
    this.editingBeneficiary = bene;
    this.editingSection = section;
  }

  // --- Remove ---

  handleRemove(bene: Beneficiary, section: BeneficiarySectionType): void {
    this.logEvent("beneficiary_remove_modal_open", {
      household_member_id: bene.householdMemberId,
      section,
    });
    this.beneficiaryToRemove = bene;
    this.removeSection = section;
    this.removeModal?.openModalDialog();
  }

  confirmRemove(): void {
    if (!this.beneficiaryToRemove || !this.removeSection) return;

    this.logEvent("beneficiary_removed", {
      household_member_id: this.beneficiaryToRemove.householdMemberId,
      section: this.removeSection,
      remaining_primary:
        this.beneficiaryState.primaryBeneficiaries.length -
        (this.removeSection === "PRIMARY" ? 1 : 0),
      remaining_backup:
        this.beneficiaryState.backupBeneficiaries.length -
        (this.removeSection === "CONTINGENT" ? 1 : 0),
    });

    this.dupDetection.remove(this.beneficiaryToRemove);

    const list = this.getBeneficiariesForSection(this.removeSection);
    this.setBeneficiariesForSection(
      this.removeSection,
      list.filter((b) => b !== this.beneficiaryToRemove),
    );

    this.beneficiaryToRemove = null;
    this.removeSection = null;

    // Reset to initial state if all beneficiaries have been removed
    if (!this.hasBeneficiaries) {
      this.addingSection = "PRIMARY";
    }

    this.emitState();
  }

  cancelRemove(): void {
    this.beneficiaryToRemove = null;
    this.removeSection = null;
  }

  // --- Duplicate detection ---

  confirmAddDuplicate(): void {
    if (!this.pendingBeneficiary || !this.pendingSaveSection) return;

    const bene = this.pendingBeneficiary;
    const section = this.pendingSaveSection;
    this.clearDuplicateState();

    // If new person, create in ZOK first even after duplicate confirmation
    if (bene.kind === "new") {
      this.createPersonInZok(bene, (savedBene) => this.commitSave(savedBene, section));
    } else {
      this.commitSave(bene, section);
    }
  }

  discardDuplicate(): void {
    this.closeInputCard();
    this.clearDuplicateState();
  }

  private clearDuplicateState(): void {
    this.pendingBeneficiary = null;
    this.pendingSaveSection = null;
    this.duplicateMatchName = "";
  }

  // --- Beneficiary member swap (person or trust dropdown change) ---

  handlePersonChange(
    event: { beneficiary: Beneficiary; memberId: string },
    section: BeneficiarySectionType,
  ): void {
    const selectedPerson = this.householdPeople.find((p) => p.householdMemberId === event.memberId);
    if (!selectedPerson) return;
    this.swapBeneficiaryMember(event, section, selectedPerson.householdMemberId, true);
  }

  handleTrustChange(
    event: { beneficiary: Beneficiary; memberId: string },
    section: BeneficiarySectionType,
  ): void {
    const selectedTrust = this.trusts.find((t) => t.householdMemberId === event.memberId);
    if (!selectedTrust) return;
    this.swapBeneficiaryMember(event, section, event.memberId, false);
  }

  private swapBeneficiaryMember(
    event: { beneficiary: Beneficiary; memberId: string },
    section: BeneficiarySectionType,
    newMemberId: string,
    checkDuplicate: boolean,
  ): void {
    const list = this.getBeneficiariesForSection(section);
    const beneIndex = list.indexOf(event.beneficiary);
    if (beneIndex < 0) return;

    const updatedBene: ExistingBeneficiary = {
      kind: "existing",
      householdMemberId: newMemberId,
      type: list[beneIndex].type,
      distributionPercentage: list[beneIndex].distributionPercentage,
      beneficiaryType: list[beneIndex].beneficiaryType,
    };

    if (checkDuplicate) {
      const otherBeneficiaries = list.filter((_, idx) => idx !== beneIndex);
      if (this.dupDetection.isDuplicateInSection(updatedBene, otherBeneficiaries)) {
        this.showDuplicatePersonError = true;
        return;
      }
      this.showDuplicatePersonError = false;
    }

    const oldBene = list[beneIndex];
    this.dupDetection.remove(oldBene);
    list[beneIndex] = updatedBene;
    this.dupDetection.add(updatedBene);
    this.emitState();
  }

  // --- Distribution ---

  handleDistributionChange(
    event: { beneficiary: Beneficiary; value: number },
    section: BeneficiarySectionType,
  ): void {
    const list = this.getBeneficiariesForSection(section);
    const beneIndex = list.indexOf(event.beneficiary);
    if (beneIndex < 0) return;

    list[beneIndex].distributionPercentage = event.value;
    this.updateDistributionErrors();
    this.emitState();
  }

  private updateDistributionErrors(): void {
    this.showPrimaryDistributionError = !this.isPrimaryValid;
    this.showBackupDistributionError = !this.isBackupValid;
    this.showZeroDistributionError = this.hasZeroDistribution;
  }

  // --- Section array accessors ---

  private getBeneficiariesForSection(section: BeneficiarySectionType): Beneficiary[] {
    return section === "PRIMARY"
      ? this.beneficiaryState.primaryBeneficiaries
      : this.beneficiaryState.backupBeneficiaries;
  }

  private setBeneficiariesForSection(section: BeneficiarySectionType, list: Beneficiary[]): void {
    if (section === "PRIMARY") {
      this.beneficiaryState.primaryBeneficiaries = list;
    } else {
      this.beneficiaryState.backupBeneficiaries = list;
    }
  }

  // --- Validation (called by parent AccountDetailsComponent) ---

  // Called by AccountDetailsComponent via @ViewChild on submit (line 433 of account-details.component.ts).
  // Returns true → parent blocks the confirm/next action.
  // Side effects are intentional: sets error flags consumed by this component's template.
  hasErrors(): boolean {
    this.showRequiredError = false;
    this.showPrimaryDistributionError = false;
    this.showBackupDistributionError = false;
    this.showZeroDistributionError = false;
    this.showIncompleteError = false;

    if (this.isInputCardOpen) {
      this.showIncompleteError = true;
      return true;
    }

    if (!this.hasBeneficiaries) {
      this.showRequiredError = true;
      return true;
    }

    this.updateDistributionErrors();
    return (
      this.showPrimaryDistributionError ||
      this.showBackupDistributionError ||
      this.showZeroDistributionError ||
      this.showDuplicatePersonError
    );
  }

  // --- Emit state ---

  private emitState(): void {
    this.beneficiaryStateChange.emit({ ...this.beneficiaryState });
    // Emit validity for parent tracking without triggering inline error flags (those are set by hasErrors() on submit)
    this.validationChange.emit(
      this.hasBeneficiaries &&
        this.isPrimaryValid &&
        this.isBackupValid &&
        !this.hasZeroDistribution,
    );
  }
}
